import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2, CreditCard, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, StatusBadge, Loading, EmptyState } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { money, fmtDay } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const STATUS_TEXT = { active: "Active", trialing: "Trial", past_due: "Payment failed - retrying", canceled: "Canceled", incomplete: "Incomplete", unpaid: "Unpaid", incomplete_expired: "Expired" };

function features(p) {
  return [
    p.max_clients ? `Up to ${p.max_clients} active clients` : "Unlimited clients",
    p.max_users > 1 ? `Up to ${p.max_users} team members` : "1 user (owner)",
    p.online_payments ? "Online card payments on invoices" : "Manual payments only (cash, check, bank)",
    "Scheduling, estimates, invoices, checklists",
  ];
}

function PlanCard({ p, current, hasSub, isOwner, busy, onPick }) {
  const isCurrent = hasSub && current === p.key;
  return (
    <div className={`bg-white border rounded-xl p-6 flex flex-col ${isCurrent ? "border-teal-600 ring-1 ring-teal-600" : "border-slate-200"}`} data-testid={`plan-card-${p.key}`}>
      <div className="font-display text-lg font-semibold text-slate-900">{p.name}</div>
      <div className="mt-2"><span className="font-display text-4xl font-semibold text-slate-900">${p.price}</span><span className="text-slate-500">/month</span></div>
      <ul className="mt-5 space-y-2 text-sm flex-1">
        {features(p).map((f) => <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-teal-600 mt-0.5 shrink-0" />{f}</li>)}
      </ul>
      <Button className="h-11 mt-6" variant={isCurrent ? "outline" : "default"} disabled={!isOwner || isCurrent || !!busy} onClick={() => onPick(p.key)} data-testid={`plan-select-${p.key}`}>
        {busy === p.key && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{isCurrent ? "Current plan" : hasSub ? `Switch to ${p.name}` : `Subscribe to ${p.name}`}
      </Button>
    </div>
  );
}

function useCheckoutReturn(onDone) {
  const [params, setParams] = useSearchParams();
  const [polling, setPolling] = useState(false);
  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("canceled")) { toast.info("Checkout canceled - no charge was made."); setParams({}); return; }
    if (!sid) return;
    setPolling(true);
    let tries = 0, stop = false;
    const tick = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/billing/checkout-status/${sid}`);
        if (data.status === "completed") { toast.success("Subscription confirmed by Stripe"); setPolling(false); setParams({}); onDone(); return; }
        if (data.status === "expired") { toast.error("Checkout expired."); setPolling(false); setParams({}); return; }
      } catch (e) { toast.error(errMsg(e)); setPolling(false); return; }
      if (++tries < 15) setTimeout(tick, 2000);
      else { setPolling(false); toast.warning("Still waiting for Stripe confirmation. Refresh in a minute."); }
    };
    tick();
    return () => { stop = true; };
  }, [params, setParams, onDone]);
  return polling;
}

export default function Billing() {
  const { reload } = useAuth();
  const [data, setData] = useState(null);
  const [hist, setHist] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/billing").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/billing/history").then((r) => setHist(r.data)).catch(() => setHist({ invoices: [], events: [] }));
    reload();
  }, [reload]);

  useEffect(() => {
    load();
  }, [load]);

  const polling = useCheckoutReturn(load);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const pick = (plan) => run(plan, async () => {
    if (data.current.has_subscription) {
      const { data: d } = await api.post("/billing/change-plan", { plan });
      setData(d); toast.success("Plan updated"); load();
    } else {
      const { data: d } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.href = d.checkout_url;
    }
  });
  const cancelToggle = (flag) => run(flag ? "cancel" : "resume", async () => {
    const { data: d } = await api.post(flag ? "/billing/cancel" : "/billing/resume");
    setData(d); toast.success(flag ? "Subscription will cancel at period end" : "Cancellation reverted"); load();
  });
  const portal = () => run("portal", async () => {
    const { data: d } = await api.post("/billing/portal", { origin_url: window.location.origin });
    window.location.href = d.url;
  });

  if (!data) return <Loading />;
  const c = data.current;
  const trialDays = c.trial_ends_at ? Math.max(0, Math.ceil((new Date(c.trial_ends_at) - new Date()) / 86400000)) : 0;

  return (
    <div data-testid="billing-page">
      <PageHeader title="Billing" subtitle="Your CleanBoss subscription. Payments from your own clients are tracked separately under Invoices." />
      {polling && <div className="rounded-lg border border-sky-200 bg-sky-50 text-sky-900 text-sm p-4 mb-4 flex items-center gap-2" data-testid="checkout-polling"><Loader2 className="h-4 w-4 animate-spin" />Confirming your subscription with Stripe…</div>}
      {!data.access.active && <div className="rounded-lg border border-red-200 bg-red-50 text-red-900 text-sm p-4 mb-4 flex items-center gap-2" data-testid="billing-inactive-alert"><AlertTriangle className="h-4 w-4" />{data.access.reason} Your data is safe and viewable, but changes are paused until you subscribe.</div>}
      <Panel className="mb-6" testId="current-plan-panel">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Current plan</div>
            <div className="font-display text-2xl font-semibold text-slate-900 capitalize" data-testid="current-plan-name">{c.plan === "trial" ? "Free trial" : c.plan}</div>
            <div className="text-sm text-slate-600 mt-1" data-testid="current-plan-status">
              {c.has_subscription ? STATUS_TEXT[c.subscription_status] || c.subscription_status : c.plan === "trial" ? `${trialDays} days left - Pro features included` : STATUS_TEXT[c.subscription_status] || "No active subscription"}
              {c.has_subscription && c.current_period_end && <> · {c.cancel_at_period_end ? "Ends" : c.subscription_status === "trialing" ? "First charge" : "Renews"} {fmtDay(c.current_period_end)}</>}
            </div>
            <div className="text-xs text-slate-500 mt-2" data-testid="plan-usage">Usage: {data.usage.active_clients}{data.limits.max_clients ? ` / ${data.limits.max_clients}` : ""} active clients · {data.usage.users} / {data.limits.max_users} users</div>
          </div>
          {c.has_subscription && data.is_owner && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="h-11" disabled={!!busy} onClick={portal} data-testid="manage-payment-btn"><CreditCard className="h-4 w-4 mr-1" />Payment method</Button>
              {c.cancel_at_period_end
                ? <Button className="h-11" disabled={!!busy} onClick={() => cancelToggle(false)} data-testid="resume-subscription-btn">Keep subscription</Button>
                : <Button variant="outline" className="h-11 text-red-700 hover:bg-red-50 hover:text-red-800" disabled={!!busy} onClick={() => cancelToggle(true)} data-testid="cancel-subscription-btn">Cancel subscription</Button>}
            </div>
          )}
        </div>
        {!data.is_owner && <p className="text-xs text-slate-500 mt-3">Only the workspace owner can change billing.</p>}
      </Panel>
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {data.plans.map((p) => <PlanCard key={p.key} p={p} current={c.plan} hasSub={c.has_subscription} isOwner={data.is_owner} busy={busy} onPick={pick} />)}
      </div>
      <p className="text-xs text-slate-500 mb-6">Test mode: use card 4242 4242 4242 4242, any future date and any CVC. Plan changes are prorated. Cancelling keeps access until the end of the paid period.</p>
      <Panel title="Billing history" testId="billing-history">
        {!hist ? <Loading /> : hist.invoices.length === 0 && hist.events.length === 0 ? <EmptyState title="No billing activity yet" /> : (
          <div className="divide-y divide-slate-100">
            {hist.invoices.map((i) => (
              <div key={i.id} className="flex items-center gap-3 py-3 text-sm" data-testid={`billing-invoice-${i.id}`}>
                <div className="flex-1"><div className="font-medium text-slate-900">{i.number || "Invoice"}</div><div className="text-xs text-slate-500">{fmtDay(i.created)}</div></div>
                <div className="font-semibold">{money(i.status === "paid" ? i.amount_paid : i.amount_due)}</div>
                <StatusBadge status={i.status === "open" ? "unpaid" : i.status} />
                {i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-teal-700" aria-label="Open invoice"><ExternalLink className="h-4 w-4" /></a>}
              </div>
            ))}
            {hist.events.map((e) => (
              <div key={e.id} className="py-2 text-xs text-slate-500" data-testid={`billing-event-${e.id}`}>{new Date(e.created_at).toLocaleString()} · {e.kind.replace(/_/g, " ")} · {e.detail}</div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
